import { Badge, Box, Flex, Image, Text, VStack } from '@chakra-ui/react';
import { Heart } from 'lucide-react';
import { KeyboardEvent, MouseEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { InternationalEventResponse, MatchState } from 'api/responseTypes';

const FONT_FAMILY = "'Thmanyah Sans', 'Noto Sans Arabic', 'Noto Sans', sans-serif";
const TEXT_PRIMARY = '#1B1917';
const CITY_COLOR = '#C03224';

interface InternationalEventCardProps {
  event: InternationalEventResponse;
  onCardClick: () => void;
}

/**
 * Vertical event card for the international events page: rounded poster with a
 * favourite heart, then title / date / location / "starting from" price below.
 */
export const InternationalEventCard = ({ event, onCardClick }: InternationalEventCardProps) => {
  const { title, imageLink, startingPrice, currency, city, country, state, date } = event;
  const isSoldOut = state === MatchState.SOLD_OUT;
  const location = [city, country].filter(Boolean).join(' - ');
  const [isFavorite, setIsFavorite] = useState(false);

  return (
    <Box
      as='li'
      listStyleType='none'
      dir='rtl'
      bg='transparent'
      cursor={isSoldOut ? 'not-allowed' : 'pointer'}
      pointerEvents={isSoldOut ? 'none' : 'unset'}
      transition='opacity 0.2s ease'
      _hover={{ opacity: isSoldOut ? 1 : 0.9 }}
      onClick={onCardClick}
      role='button'
      tabIndex={isSoldOut ? -1 : 0}
      aria-disabled={isSoldOut}
      aria-label={`${title}${date ? ` - ${date}` : ''}${city ? ` - ${city}` : ''}`}
      onKeyDown={(e: KeyboardEvent) => {
        if ((e.key === 'Enter' || e.key === ' ') && !isSoldOut) {
          e.preventDefault();
          onCardClick();
        }
      }}
    >
      {/* Poster */}
      <Box
        position='relative'
        borderRadius='16px'
        overflow='hidden'
        bg='dark.D100'
        sx={{ aspectRatio: '4 / 5' }}
      >
        <Image
          src={imageLink ?? undefined}
          alt={title}
          width='100%'
          height='100%'
          objectFit='cover'
          filter={isSoldOut ? 'grayscale(100%) opacity(0.8)' : 'none'}
        />

        {/* Favourite heart (visual toggle) */}
        <Flex
          as='button'
          type='button'
          position='absolute'
          top={3}
          insetStart={3}
          boxSize='40px'
          borderRadius='full'
          align='center'
          justify='center'
          bg='rgba(255, 255, 255, 0.85)'
          _hover={{ bg: 'white' }}
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            setIsFavorite((v) => !v);
          }}
          aria-label='المفضلة'
          aria-pressed={isFavorite}
        >
          <Heart
            size={20}
            color={isFavorite ? CITY_COLOR : TEXT_PRIMARY}
            fill={isFavorite ? CITY_COLOR : 'none'}
          />
        </Flex>

        {isSoldOut && (
          <Badge
            position='absolute'
            top={3}
            insetEnd={3}
            bg='red'
            color='white'
            px={3}
            py={1}
            borderRadius='full'
            textStyle='bodySmallSemibold'
          >
            <FormattedMessage defaultMessage='Sold Out' id='concerts.soldOut' />
          </Badge>
        )}
      </Box>

      {/* Details */}
      <VStack align='stretch' spacing={1} mt={3}>
        <Text
          fontFamily={FONT_FAMILY}
          fontWeight={700}
          fontSize={{ base: '16px', md: '18px' }}
          lineHeight='1.4'
          letterSpacing='-0.01em'
          textAlign='right'
          color={TEXT_PRIMARY}
          noOfLines={2}
        >
          {title}
        </Text>

        {date && (
          <Text
            fontFamily={FONT_FAMILY}
            fontWeight={400}
            fontSize='14px'
            lineHeight='1.6'
            textAlign='right'
            color={isSoldOut ? 'dark.D400' : TEXT_PRIMARY}
          >
            {date}
          </Text>
        )}

        {location && (
          <Text
            fontFamily={FONT_FAMILY}
            fontWeight={400}
            fontSize='14px'
            lineHeight='1.6'
            textAlign='right'
            color={isSoldOut ? 'dark.D500' : CITY_COLOR}
            noOfLines={1}
          >
            {location}
          </Text>
        )}

        {!isSoldOut && (
          <Text
            fontFamily={FONT_FAMILY}
            fontWeight={400}
            fontSize='14px'
            lineHeight='1.6'
            textAlign='right'
            color='dark.D500'
          >
            <FormattedMessage defaultMessage='تبدأ من' id='internationalEvents.startingFrom' />{' '}
            <Text as='span' fontWeight={700} color={TEXT_PRIMARY}>
              {`${new Intl.NumberFormat().format(startingPrice)} ${currency}`}
            </Text>
          </Text>
        )}
      </VStack>
    </Box>
  );
};
